// ---- Gift modal (dialogue giveItem → Take it) ----
// Reusable hand-off UI: an NPC offers one or more free items via a `giveItem`
// talk action; this panel teaches the bag (offered slots + a read-only mirror
// of the player's 10 inventory cells) and sends `acceptGift` when the player
// takes something. Server authorizes against the live talk lock + the partner's
// talk graph (`offersGift`) — same pattern as shops. Conversation stays open
// underneath (shop twin); Esc closes just the gift panel.
//
// Authoring: `{ "action": { "kind": "giveItem", "type": "fishing-rod" }, "to": "next" }`
// — the choice opens this panel and may route to the post-gift node; Take it
// grants the item, refreshes the bag mirror, then auto-closes so dialogue
// continues.

import { $ } from "./dom.ts";
import { itemSpec } from "./items/index.ts";
import { countOf, hasFreeSlot, closeInventory, bagSlotSnapshot } from "./inventory.ts";
import type { ItemType } from "./shared.ts";

const BAG_SLOTS = 10;

let panel: HTMLElement;
let titleEl: HTMLElement;
let offerEl: HTMLElement;
let bagEl: HTMLElement;
let takeBtn: HTMLButtonElement;
let hintEl: HTMLElement;

/** Offered types still unclaimed in this panel session (usually one). */
let offered: ItemType[] = [];
/** Currently selected offered type (click a gift slot). */
let selected: ItemType | null = null;
/** Intent sender — wired at init (net.ts `sendAcceptGift`). */
let onTake: (type: ItemType) => void = () => {};
/** True while waiting for the inventory echo after Take it. */
let pendingType: ItemType | null = null;

export function initGift(handlers: { onTake: (type: ItemType) => void }): void {
  onTake = handlers.onTake;
  panel = $<HTMLElement>("#gift");
  titleEl = panel.querySelector(".gift-title") as HTMLElement;
  offerEl = $<HTMLElement>("#giftOffer");
  bagEl = $<HTMLElement>("#giftBag");
  takeBtn = $<HTMLButtonElement>("#giftTake");
  hintEl = panel.querySelector(".gift-hint") as HTMLElement;
  $<HTMLButtonElement>("#giftClose").addEventListener("click", closeGift);
  takeBtn.addEventListener("click", takeSelected);

  // Esc closes the gift (NOT the underlying conversation). Registered before
  // initTalk so this wins over talk.ts Esc→endTalk (same shop pattern).
  addEventListener("keydown", (e) => {
    if (!giftOpen()) return;
    if (e.key === "Escape") { e.stopImmediatePropagation(); closeGift(); }
    else if (e.key >= "1" && e.key <= "9") e.stopImmediatePropagation();
  });
}

export function giftOpen(): boolean {
  return offered.length > 0 || pendingType !== null;
}

/**
 * Open the gift panel for the given item type(s). Fired from talk.ts's
 * `giveItem` action. Multiple types are supported for future multi-gift
 * nodes; current content uses one.
 */
export function openGift(types: ItemType | ItemType[], giverName?: string): void {
  const list = (Array.isArray(types) ? types : [types]).filter(Boolean);
  if (!list.length) return;
  offered = [...list];
  selected = offered.length === 1 ? offered[0]! : null;
  pendingType = null;
  titleEl.textContent = giverName ? `${giverName} offers` : "A gift";
  hintEl.textContent = "Click the item, then Take it — it will land in your bag below.";
  // Tutorial: close the corner bag so the modal is the one inventory surface.
  closeInventory();
  panel.style.display = "flex";
  render();
}

export function closeGift(): void {
  offered = [];
  selected = null;
  pendingType = null;
  panel.style.display = "none";
}

/** Re-render after an inventory echo. If we were waiting on a take and the bag
 *  now holds it, auto-close (success path). No-op when closed. */
export function refreshGiftIfOpen(): void {
  if (!giftOpen()) return;
  if (pendingType && countOf(pendingType) > 0) {
    // Claimed — drop from offered; close when nothing left to take.
    offered = offered.filter((t) => t !== pendingType);
    pendingType = null;
    selected = null;
    if (offered.length === 0) {
      closeGift();
      return;
    }
  }
  render();
}

function takeSelected(): void {
  if (!selected || pendingType) return;
  if (!hasFreeSlot(selected) && countOf(selected) < 1) {
    // Bag full and we don't already hold one — tell the player instead of a
    // dead button so the tutorial can't stall silently.
    hintEl.textContent = "Your bag is full — make room, then Take it.";
    return;
  }
  pendingType = selected;
  takeBtn.disabled = true;
  takeBtn.textContent = "…";
  onTake(selected);
  // Success path closes via refreshGiftIfOpen on the inventory echo. A rejected
  // take (full bag / no talk lock) leaves the panel open; re-enable after a beat.
  setTimeout(() => {
    if (pendingType === selected) {
      pendingType = null;
      render();
    }
  }, 1500);
}

function render(): void {
  if (!giftOpen()) return;
  offerEl.replaceChildren();
  for (const type of offered) {
    const spec = itemSpec(type);
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = "gift-slot" + (selected === type ? " sel" : "");
    cell.title = spec.name;
    cell.innerHTML =
      `<span class="gift-icon">${spec.icon}</span>` +
      `<span class="gift-name">${spec.name}</span>`;
    cell.addEventListener("click", () => {
      selected = type;
      render();
    });
    offerEl.appendChild(cell);
  }

  // Read-only bag mirror — same 10-slot grid the corner bag uses.
  bagEl.replaceChildren();
  const snap = bagSlotSnapshot();
  for (let i = 0; i < BAG_SLOTS; i++) {
    const cell = document.createElement("div");
    cell.className = "gift-bagslot";
    const s = snap[i];
    if (s) {
      const spec = itemSpec(s.type);
      cell.classList.add("filled");
      cell.innerHTML =
        `<span class="gift-icon">${spec.icon}</span>` +
        (s.count > 1 ? `<span class="gift-count">×${s.count}</span>` : "");
      cell.title = spec.name;
    }
    bagEl.appendChild(cell);
  }

  const canTake = !!selected && !pendingType && (hasFreeSlot(selected) || countOf(selected) > 0);
  takeBtn.disabled = !canTake;
  takeBtn.textContent = pendingType ? "…" : "Take it";
}
